/**
 * Abstraction over network object
 * to have hex/dex/scan functions in one place
 */

import { ITicker } from "..";
import { chainIds, chainMapping } from "../enums/blockchains";
import { mapping } from "../helpers/networkMapping";
import { ICommonBase } from "../interfaces/common";

class Network {
  chain: number;

  constructor(kind: number) {
    this.chain = kind;
  }

  static createETH = (): Network => {
    return new Network(chainMapping['ETH']);
  };

  static fromChainId = (chainId: number | undefined): Network | undefined => {
    if (
      chainId &&
      chainIds.includes(chainId)
    ) {
      return new Network(chainId);
    }
    return undefined;
  };

  static fromChain = (chain: string): Network | undefined => {
    const chainId = chainMapping[chain];
    if (chainId) {
      return this.fromChainId(chainId);
    }
    return new Network(chainMapping['ETH']);
  };

  static fromTicker = (ticker: ITicker): Network | undefined => {
    const { chain } = ticker
    return this.fromChain(chain)
  }

  getName = (): string => {
    return this.chain ? mapping[this.chain].name : "Ethereum Mainnet";
  };

  getScan = (): string => {
    return this.chain
      ? mapping[this.chain].scan
      : "https://etherscan.io/address/";
  };

  getScanForWallet = (): string => {
    return this.chain
      ? mapping[this.chain].walletScan
      : "https://etherscan.io/address/";
  };

  getScanForToken = (token: string): string => {
    return this.chain
      ? mapping[this.chain].tokenScan.split("tokenName").join(token)
      : `https://etherscan.io/token/${token}`;
  };

  // https://api.coingecko.com/api/v3/asset_platforms
  toCoingecko = (): string => {
    return this.chain ? mapping[this.chain].coingecko : "ethereum";
  };

  toCovalentNetwork = (): string => {
    if (this.chain) {
      const bc = mapping[this.chain];
      return bc.covalent ? bc.covalent : "ETH";
    }
    return "ETH";
  };

  toTrustWallet = (): string => {
    return this.chain ? mapping[this.chain].trustwallet : "ethereum";
  };

  toHex = (): string => {
    return this.chain ? mapping[this.chain].hex : "0x1";
  };

  toDecimal = () => {
    return this.chain ? mapping[this.chain].decimals : 1;
  };

  /**
   * Support for short symbol, used in UI like icons mapping
   * @returns
   */
  getSymbol = (): string =>
    mapping[this.chain].symbol;

  /**
   * Return short symbol to match metamask
   * @returns
   */
  getNativeCurrencySymbol = (): string =>
    mapping[this.chain].nativeCurrency;
  /**
   * Support for default RPC
   * @returns
   */
  getRPC = (): string => {
    return this.chain ? mapping[this.chain].rpc : "";
  };

  getCommonBases = (): ICommonBase[] => {
    return this.chain ? mapping[this.chain].commonBases : [];
  };

  isETH = () => this.chain === chainMapping.ETH;

  isBSC = () => this.chain === chainMapping.BSC;

  isBSCTestnet = () => this.chain === chainMapping.BSC_TESTNET;

  isHECO = () => this.chain === chainMapping.HECO;

  isELROND = () => this.chain === chainMapping.ELROND;

  isAVAX = () => this.chain === chainMapping.AVAX;

  isMETER = () => this.chain === chainMapping.METER;

  isFANTOM = () => this.chain === chainMapping.FANTOM;

  isBTTC = () => this.chain === chainMapping.BTTC;

  isTron = () => this.chain === chainMapping.TRON;

  isPOLYGON = () => this.chain === chainMapping.POLYGON;

  isOKX = () => this.chain === chainMapping.OKX;
}

export default Network;
